import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#syncEveryLegendHeaderOverlays();
    window.addEventListener('resize', this.#syncEveryLegendHeaderOverlays, { passive: true });
    window.addEventListener('scroll', this.#syncEveryLegendHeaderOverlays, { passive: true });
    document.addEventListener('click', this.#syncEveryLegendHeaderOverlays, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.removeEventListener('resize', this.#syncEveryLegendHeaderOverlays);
    window.removeEventListener('scroll', this.#syncEveryLegendHeaderOverlays);
    document.removeEventListener('click', this.#syncEveryLegendHeaderOverlays, true);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  #syncEveryLegendHeaderOverlays = () => {
    requestAnimationFrame(() => {
      const anchor =
        document.querySelector('#header-component .header__row') ||
        document.querySelector('#header-component .header__columns') ||
        document.querySelector('#header-component');
      const top = anchor ? Math.max(0, Math.round(anchor.getBoundingClientRect().top)) : 0;
      document.documentElement.style.setProperty('--everylegend-cart-drawer-top', `${top}px`);

      const cartDialog = document.querySelector('#cart-drawer .theme-drawer__dialog');
      if (cartDialog instanceof HTMLElement) {
        cartDialog.style.setProperty('top', `var(--everylegend-cart-drawer-top, ${top}px)`, 'important');
        cartDialog.style.setProperty('height', `calc(100dvh - var(--everylegend-cart-drawer-top, ${top}px))`, 'important');
        cartDialog.style.setProperty('border-top', '1px solid var(--etsy-ink, #222222)', 'important');
      }

      const localizationWrapper = document.querySelector('dropdown-localization-component .localization-wrapper');
      if (localizationWrapper instanceof HTMLElement) {
        localizationWrapper.style.setProperty('position', 'absolute', 'important');
        localizationWrapper.style.setProperty('top', 'calc(100% + 10px)', 'important');
        localizationWrapper.style.setProperty('right', '0', 'important');
        localizationWrapper.style.setProperty('left', 'auto', 'important');
        localizationWrapper.style.setProperty('z-index', '1600', 'important');
        localizationWrapper.style.setProperty('width', 'min(390px, calc(100vw - 24px))', 'important');
        localizationWrapper.style.setProperty('max-height', 'min(27.5rem, calc(100vh - 132px))', 'important');
        localizationWrapper.style.setProperty('overflow', 'auto', 'important');
        localizationWrapper.style.setProperty('transform', 'none', 'important');
      }
    });
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

const EVERYLEGEND_ACCOUNT_MODAL_ID = 'EveryLegendAccountDialog';

function ensureEveryLegendAccountModal() {
  let dialog = document.getElementById(EVERYLEGEND_ACCOUNT_MODAL_ID);
  if (dialog instanceof HTMLDialogElement) return dialog;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <dialog id="${EVERYLEGEND_ACCOUNT_MODAL_ID}" class="everylegend-account-dialog" aria-labelledby="EveryLegendAccountDialogTitle">
      <div class="everylegend-account-dialog__panel">
        <button type="button" class="everylegend-account-dialog__close button-unstyled" aria-label="Close account dialog" data-everylegend-account-close>
          <span aria-hidden="true">&times;</span>
        </button>
        <h2 id="EveryLegendAccountDialogTitle">Sign in or create an account to start shopping</h2>
        <form class="everylegend-account-dialog__form" method="get" action="/account/login">
          <label for="EveryLegendAccountEmail">Email address<span aria-hidden="true">*</span></label>
          <input id="EveryLegendAccountEmail" type="email" name="email" autocomplete="email" placeholder="Enter your email" required>
          <button type="submit">Continue</button>
        </form>
        <a class="everylegend-account-dialog__trouble" href="/account/login">Trouble signing in?</a>
        <p class="everylegend-account-dialog__terms">
          By clicking Continue, you agree to EveryLegend's
          <a href="/policies/terms-of-service">Terms of Service</a>
          and
          <a href="/policies/privacy-policy">Privacy Policy</a>.
        </p>
        <div class="everylegend-account-dialog__divider" aria-hidden="true"><span></span><em>or</em><span></span></div>
        <div class="everylegend-account-dialog__socials" aria-label="Additional sign-in options">
          <a href="/account/login"><span class="everylegend-account-dialog__brand everylegend-account-dialog__brand--google" aria-hidden="true">G</span><span>Continue with Google</span></a>
          <a href="/account/login"><span class="everylegend-account-dialog__brand everylegend-account-dialog__brand--facebook" aria-hidden="true">f</span><span>Continue with Facebook</span></a>
          <a href="/account/login"><span class="everylegend-account-dialog__brand everylegend-account-dialog__brand--apple" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M16.64 12.15c-.02-2.24 1.83-3.31 1.91-3.36-1.04-1.52-2.66-1.73-3.23-1.75-1.38-.14-2.69.81-3.39.81-.7 0-1.78-.79-2.93-.77-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.75 1.13 8.96.75 1.08 1.64 2.3 2.81 2.25 1.13-.05 1.56-.73 2.92-.73s1.75.73 2.94.71c1.21-.02 1.98-1.1 2.72-2.19.86-1.26 1.22-2.48 1.24-2.54-.03-.01-2.37-.91-2.4-3.62ZM14.42 5.58c.62-.75 1.04-1.79.92-2.83-.89.04-1.97.59-2.61 1.34-.57.66-1.08 1.72-.94 2.73.99.08 2.01-.5 2.63-1.24Z" fill="currentColor"/></svg></span><span>Continue with Apple</span></a>
        </div>
        <p class="everylegend-account-dialog__note">
          EveryLegend may contact you about your account and orders. You can update preferences anytime, and we will never post without your permission.
        </p>
      </div>
    </dialog>
  `;
  dialog = wrapper.firstElementChild;
  if (!(dialog instanceof HTMLDialogElement)) return null;

  document.body.append(dialog);
  dialog.querySelector('[data-everylegend-account-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  return dialog;
}

function injectEveryLegendAccountModalStyles() {
  if (document.getElementById('EveryLegendAccountDialogStyles')) return;
  const style = document.createElement('style');
  style.id = 'EveryLegendAccountDialogStyles';
  style.textContent = `
    .everylegend-account-dialog{width:min(92vw,520px);max-height:min(92svh,760px);border:0;border-radius:22px;padding:0;background:transparent;color:var(--color-foreground,#222);box-shadow:0 22px 70px rgb(0 0 0 / 24%);z-index:1800}
    .everylegend-account-dialog::backdrop{background:rgb(0 0 0 / 38%)}
    .everylegend-account-dialog__panel{position:relative;display:grid;gap:1.25rem;max-height:min(92svh,760px);overflow:auto;border:1px solid rgb(34 34 34 / 12%);border-radius:22px;padding:clamp(1.65rem,4vw,2.35rem);background:#fff;-webkit-overflow-scrolling:touch}
    .everylegend-account-dialog__close{position:absolute;top:.75rem;right:.75rem;display:inline-grid;place-items:center;width:36px;height:36px;border-radius:50%;color:rgb(34 34 34 / 72%);font-size:1.45rem;line-height:1}
    .everylegend-account-dialog__close:is(:hover,:focus-visible){background:rgb(34 34 34 / 8%);color:#222}
    .everylegend-account-dialog h2{max-width:13.5em;margin:0;padding-right:2rem;color:var(--etsy-ink,#222);font-family:var(--font-heading--family);font-size:clamp(1.55rem,4vw,2rem);font-weight:800;line-height:1.16;letter-spacing:0}
    .everylegend-account-dialog__form{display:grid;gap:.8rem}
    .everylegend-account-dialog__form label{color:var(--etsy-ink,#222);font-size:1.08rem;font-weight:800;line-height:1.2}
    .everylegend-account-dialog__form label span{color:#c41230}
    .everylegend-account-dialog__form input{width:100%;min-height:58px;border:2px solid var(--etsy-field-border,#9a8f89);border-radius:10px;padding:0 1rem;background:#fff;color:var(--etsy-ink,#222);font:inherit;font-size:1rem;outline:none}
    .everylegend-account-dialog__form input:focus{border-color:var(--etsy-ink,#222);box-shadow:0 0 0 3px rgb(51 42 56 / 14%)}
    .everylegend-account-dialog__form button{min-height:62px;border:0;border-radius:999px;background:var(--etsy-button,#332a38);color:#fff;font-size:1.08rem;font-weight:800;cursor:pointer}
    .everylegend-account-dialog__form button:is(:hover,:focus-visible){background:#241d29}
    .everylegend-account-dialog__trouble{justify-self:center;color:var(--etsy-ink,#222);font-size:.98rem;text-decoration:underline;text-underline-offset:.18em}
    .everylegend-account-dialog__terms,.everylegend-account-dialog__note{margin:0;color:rgb(34 34 34 / 70%);font-size:.9rem;line-height:1.36;text-align:center}
    .everylegend-account-dialog__terms a{color:var(--etsy-blue,#3b67d9);text-decoration:underline;text-underline-offset:.15em}
    .everylegend-account-dialog__divider{display:grid;grid-template-columns:1fr auto 1fr;gap:1rem;align-items:center;color:rgb(34 34 34 / 65%)}
    .everylegend-account-dialog__divider span{height:1px;background:rgb(34 34 34 / 16%)}
    .everylegend-account-dialog__divider em{font-style:normal;font-size:.95rem}
    .everylegend-account-dialog__socials{display:grid;gap:.7rem}
    .everylegend-account-dialog__socials a{display:grid;grid-template-columns:1.6rem 1fr;align-items:center;min-height:56px;border:2px solid var(--etsy-button,#332a38);border-radius:999px;padding:0 1.1rem;color:var(--etsy-button,#332a38);font-size:1rem;font-weight:800;line-height:1.15;text-align:center;text-decoration:none}
    .everylegend-account-dialog__socials a:is(:hover,:focus-visible){background:rgb(51 42 56 / 6%)}
    .everylegend-account-dialog__brand{display:inline-grid;place-items:center;width:1.35rem;height:1.35rem;justify-self:end;font-weight:900;line-height:1}
    .everylegend-account-dialog__brand--google{color:#4285f4;font-family:Arial,sans-serif}
    .everylegend-account-dialog__brand--facebook{color:#4267b2;font-family:Arial,sans-serif;font-size:1.55rem}
    .everylegend-account-dialog__brand--apple svg{width:1.35rem;height:1.35rem}
    @media screen and (max-width:749px){.everylegend-account-dialog{width:min(94vw,430px);border-radius:20px}.everylegend-account-dialog__panel{gap:1rem;border-radius:20px;padding:1.45rem 1.2rem 1.35rem}.everylegend-account-dialog h2{font-size:1.55rem}.everylegend-account-dialog__form input{min-height:54px}.everylegend-account-dialog__form button{min-height:56px}.everylegend-account-dialog__socials a{min-height:52px;font-size:.95rem}}
  `;
  document.head.append(style);
}

function openEveryLegendAccountModal() {
  injectEveryLegendAccountModalStyles();
  const dialog = ensureEveryLegendAccountModal();
  if (!dialog) return;
  if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
  else dialog.setAttribute('open', '');
  window.setTimeout(() => dialog.querySelector('input[type="email"]')?.focus(), 80);
}

document.addEventListener(
  'click',
  (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const pathTarget = path.find((node) => node instanceof Element && node.matches?.('shopify-account'));
    if (pathTarget) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openEveryLegendAccountModal();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;
    let accountTarget = target.closest('shopify-account');
    if (!accountTarget) {
      const accountButton = target.closest('.account-button');
      if (accountButton?.querySelector('shopify-account')) accountTarget = accountButton;
    }
    if (!accountTarget) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openEveryLegendAccountModal();
  },
  true
);
